import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ContactTypeBadge,
  ConfidenceBadge,
  isGenericContact,
} from '../components/DiscoveredContactsPanel';
import { Check, Export, MagnifyingGlass, PaperPlaneTilt, SpinnerGap, UsersThree } from '@phosphor-icons/react';
import { useSaaSAuth } from '../contexts/SaaSAuthContext';
import { featureGate, quotaGate } from '../lib/entitlements';

const CREATE_NEW_GROUP = '__create_new_group__';

function splitName(contact) {
  const first = (contact.first_name || '').trim();
  const last = (contact.last_name || '').trim();
  if (first || last) return { first, last };
  const full = (contact.full_name || '').trim();
  if (!full) return { first: '', last: '' };
  const parts = full.split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') };
}

/** Personal for companies that have them; otherwise that company's generics. */
export function buildDefaultSelection(contacts) {
  const withEmail = (contacts || []).filter((c) => (c.email || '').trim());
  const byCompany = new Map();
  for (const c of withEmail) {
    const key = c.company_id || '_none';
    if (!byCompany.has(key)) byCompany.set(key, []);
    byCompany.get(key).push(c);
  }

  const ids = new Set();
  let usedGenericFallback = false;
  for (const list of byCompany.values()) {
    const personal = list.filter((c) => !isGenericContact(c));
    const generic = list.filter((c) => isGenericContact(c));
    if (personal.length > 0) {
      personal.forEach((c) => ids.add(c.id));
    } else if (generic.length > 0) {
      generic.forEach((c) => ids.add(c.id));
      usedGenericFallback = true;
    }
  }
  return { ids, usedGenericFallback };
}

export default function ReviewContactsPage() {
  const [searchParams] = useSearchParams();
  const focusCompanyId = searchParams.get('company_id') || '';
  const autoOpenPush = searchParams.get('action') === 'push';
  const { user } = useSaaSAuth();
  const pushFeature = featureGate(user, 'has_pro_email_integration');
  const pushQuota = quotaGate(user, 'pushed_leads', 'max_pushed_leads');
  const pushBlocked = !pushFeature.allowed || !pushQuota.allowed;
  const pushBlockLabel = !pushFeature.allowed ? pushFeature.label : pushQuota.label;

  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [usedGenericFallback, setUsedGenericFallback] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportName, setExportName] = useState('Contact Export');
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [proEmailGroups, setProEmailGroups] = useState([]);
  const [destinationGroupId, setDestinationGroupId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [autoPushHandled, setAutoPushHandled] = useState(false);
  const creatingNewGroup = destinationGroupId === CREATE_NEW_GROUP;
  const pushOverQuota = pushQuota.remaining !== Number.POSITIVE_INFINITY && selected.size > pushQuota.remaining;

  const fetchAllContacts = useCallback(async () => {
    setLoading(true);
    try {
      const collected = [];
      let page = 1;
      let reportedTotal = 0;
      const pageSize = 500;
      // Paginate until we have everything (caps at ~10k for safety).
      while (page <= 20) {
        const params = { page, limit: pageSize };
        if (search.trim()) params.search = search.trim();
        if (focusCompanyId) params.company_id = focusCompanyId;
        const { data } = await api.get('/contacts', { params });
        const items = data.items || [];
        reportedTotal = data.total || items.length;
        collected.push(...items);
        if (collected.length >= reportedTotal || items.length < pageSize) break;
        page += 1;
      }
      setContacts(collected);
      setTotal(reportedTotal);
      const { ids, usedGenericFallback: fallback } = buildDefaultSelection(collected);
      setSelected(ids);
      setUsedGenericFallback(fallback);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [search, focusCompanyId]);

  useEffect(() => {
    fetchAllContacts();
  }, [fetchAllContacts]);

  const personalContacts = useMemo(
    () => contacts.filter((c) => c.email && !isGenericContact(c)),
    [contacts]
  );
  const genericContacts = useMemo(
    () => contacts.filter((c) => c.email && isGenericContact(c)),
    [contacts]
  );
  const companiesScanned = useMemo(() => {
    const ids = new Set(contacts.map((c) => c.company_id).filter(Boolean));
    return ids.size;
  }, [contacts]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectPersonal = () => {
    setSelected(new Set(personalContacts.map((c) => c.id)));
    setUsedGenericFallback(false);
  };
  const selectGeneric = () => {
    setSelected(new Set(genericContacts.map((c) => c.id)));
    setUsedGenericFallback(false);
  };
  const selectAll = () => {
    setSelected(new Set(contacts.filter((c) => c.email).map((c) => c.id)));
    setUsedGenericFallback(false);
  };
  const clearSelection = () => {
    setSelected(new Set());
    setUsedGenericFallback(false);
  };

  const runExport = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one contact to export');
      return;
    }
    setExporting(true);
    try {
      const { data } = await api.post('/exports/csv', {
        name: exportName || 'Contact Export',
        export_type: 'csv',
        destination: 'download',
        contact_ids: [...selected],
      });
      toast.success(`Exported ${data.record_count} contacts`);
      if (data.csv_content && data.csv_content !== 'No matching records found') {
        const blob = new Blob([data.csv_content], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${exportName || 'Contact Export'}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const openPushDialog = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one contact to push');
      return;
    }
    if (pushBlocked) {
      toast.error(pushBlockLabel);
      return;
    }
    if (pushOverQuota) {
      toast.error(`Your plan has only ${pushQuota.remaining} Pro-Email pushes remaining.`);
      return;
    }
    setShowPushDialog(true);
    setLoadingGroups(true);
    setProEmailGroups([]);
    setDestinationGroupId('');
    setNewGroupName('');
    try {
      const { data } = await api.get('/integrations/pro-email/groups');
      setProEmailGroups(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoadingGroups(false);
    }
  };

  const pushSelectedToProEmail = async () => {
    if (selected.size === 0 || !destinationGroupId || pushing) return;
    if (creatingNewGroup && !newGroupName.trim()) return;
    setPushing(true);
    try {
      let groupId = destinationGroupId;
      let groupName = proEmailGroups.find((group) => group.id === destinationGroupId)?.name || '';
      if (creatingNewGroup) {
        const { data: createdGroup } = await api.post('/integrations/pro-email/groups', {
          name: newGroupName.trim(),
          description: 'Created from Lead Creator contact selection',
        });
        groupId = createdGroup.id;
        groupName = createdGroup.name;
      }

      const { data } = await api.post('/integrations/pro-email/push', {
        contact_ids: [...selected],
        destination_group_id: groupId,
        locale: 'en',
      });
      const summary = `${data.pushed || 0} contact${data.pushed === 1 ? '' : 's'} pushed to ${data.group_name || groupName}`
        + `${data.skipped ? `, ${data.skipped} skipped` : ''}`
        + `${data.failed ? `, ${data.failed} failed` : ''}.`;
      if (data.failed) toast.error(summary);
      else toast.success(summary);
      setShowPushDialog(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPushing(false);
    }
  };

  useEffect(() => {
    if (!loading && autoOpenPush && !autoPushHandled && selected.size > 0) {
      setAutoPushHandled(true);
      openPushDialog();
    }
  }, [loading, autoOpenPush, autoPushHandled, selected.size]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectableCount = contacts.filter((c) => c.email).length;

  return (
    <div data-testid="review-contacts-page">
      <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">
            Review Contacts
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Select contacts once, then export them as CSV or push that exact selection to Pro-Email.
            Personal contacts are selected by default; generics are used only when a company has no personal contacts.
          </p>
          {focusCompanyId && (
            <p className="text-xs font-mono text-sky-700 mt-2">
              Filtered to company from Find Contacts ·{' '}
              <Link to="/app/review-contacts" className="underline">
                Show all
              </Link>
            </p>
          )}
        </div>
        <Button asChild variant="outline" className="shrink-0">
          <Link to="/app/exports" data-testid="review-contacts-exports-link">
            Export history
          </Link>
        </Button>
      </div>

      {/* Discovery Results summary */}
      <div
        className="rounded-md border border-gray-200 bg-white p-4 mb-4"
        data-testid="review-contacts-summary"
      >
        <h2 className="text-sm font-heading font-semibold text-gray-900 mb-3">Discovery Results</h2>
        <ul className="space-y-1.5 text-sm text-gray-700 mb-4">
          <li className="flex items-center gap-2" data-testid="summary-companies-scanned">
            <Check size={16} className="text-emerald-600 shrink-0" weight="bold" />
            <span>
              <span className="font-mono font-semibold">{companiesScanned}</span> Companies scanned
            </span>
          </li>
          <li className="flex items-center gap-2" data-testid="summary-personal-count">
            <Check size={16} className="text-emerald-600 shrink-0" weight="bold" />
            <span>
              <span className="font-mono font-semibold">{personalContacts.length}</span> Personal contacts found
            </span>
          </li>
          <li className="flex items-center gap-2" data-testid="summary-generic-count">
            <Check size={16} className="text-emerald-600 shrink-0" weight="bold" />
            <span>
              <span className="font-mono font-semibold">{genericContacts.length}</span> Generic addresses found
            </span>
          </li>
        </ul>
        <p className="text-base font-heading font-semibold text-gray-900" data-testid="summary-selected-count">
          Selected:{' '}
          <span className="font-mono text-emerald-700">{selected.size}</span>
        </p>
      </div>

      {usedGenericFallback && personalContacts.length === 0 && (
        <div
          className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="generic-fallback-message"
        >
          No personal contacts were found. Generic company addresses have been selected.
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9 bg-white"
            placeholder="Search name, email, or title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="review-contacts-search"
          />
        </div>
        <Button type="button" variant="outline" size="sm" onClick={selectPersonal} data-testid="select-personal-btn">
          Select Personal
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={selectGeneric} data-testid="select-generic-btn">
          Select Generic
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
          Select All
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
          Clear Selection
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4 p-3 rounded-md border border-gray-200 bg-white">
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-mono uppercase text-gray-500 mb-1 block">Export name</label>
          <Input
            value={exportName}
            onChange={(e) => setExportName(e.target.value)}
            data-testid="review-export-name"
          />
        </div>
        <Button
          onClick={runExport}
          disabled={exporting || selected.size === 0}
          className="bg-black text-white hover:bg-gray-800"
          data-testid="review-export-selected-btn"
        >
          {exporting ? (
            <>
              <SpinnerGap size={16} className="mr-2 animate-spin" /> Exporting…
            </>
          ) : (
            <>
              <Export size={16} className="mr-2" /> Export {selected.size} Contact{selected.size === 1 ? '' : 's'}
            </>
          )}
        </Button>
        <Button
          onClick={openPushDialog}
          disabled={pushing || selected.size === 0 || pushBlocked || pushOverQuota}
          variant="outline"
          data-testid="review-push-selected-btn"
          title={pushBlocked ? pushBlockLabel : undefined}
        >
          <PaperPlaneTilt size={16} className="mr-2" />
          Push {selected.size} Selected to Pro-Email
        </Button>
      </div>

      <Dialog open={showPushDialog} onOpenChange={(open) => !pushing && setShowPushDialog(open)}>
        <DialogContent className="sm:max-w-lg" data-testid="review-pro-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Push selected contacts to Pro-Email</DialogTitle>
            <DialogDescription>
              Your exact selection of {selected.size} contact{selected.size === 1 ? '' : 's'} will be sent to one Pro-Email group.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.1em] text-gray-500 mb-1 block">
                Destination group
              </label>
              <Select value={destinationGroupId} onValueChange={setDestinationGroupId} disabled={loadingGroups || pushing}>
                <SelectTrigger data-testid="review-pro-email-group-select">
                  <SelectValue placeholder={loadingGroups ? 'Loading groups…' : 'Choose a group'} />
                </SelectTrigger>
                <SelectContent>
                  {proEmailGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name} ({group.member_count || 0})
                    </SelectItem>
                  ))}
                  <SelectItem value={CREATE_NEW_GROUP}>Create a new Pro-Email group</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {creatingNewGroup && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-4 space-y-2">
                <label className="text-xs font-mono uppercase tracking-[0.1em] text-blue-800 block">
                  New group name
                </label>
                <Input
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                  placeholder="e.g. Potential Pro-Email Clients"
                  disabled={pushing}
                  maxLength={200}
                  data-testid="review-new-pro-email-group-name"
                />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowPushDialog(false)} disabled={pushing}>
                Cancel
              </Button>
              <Button
                onClick={pushSelectedToProEmail}
                disabled={loadingGroups || !destinationGroupId || (creatingNewGroup && !newGroupName.trim()) || pushing}
                className="bg-black text-white hover:bg-gray-800"
                data-testid="review-confirm-pro-email-push"
              >
                {pushing ? (
                  <><SpinnerGap size={16} className="mr-2 animate-spin" /> Pushing…</>
                ) : creatingNewGroup ? (
                  'Create group and push'
                ) : (
                  `Push ${selected.size} selected`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="border border-gray-200 rounded-md bg-white overflow-x-auto">
        <table className="w-full min-w-[900px]" data-testid="review-contacts-table">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="w-10 py-3 px-4">
                <Checkbox
                  checked={selectableCount > 0 && selected.size === selectableCount}
                  onCheckedChange={(checked) => (checked ? selectAll() : clearSelection())}
                  data-testid="review-select-all-checkbox"
                />
              </th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">First Name</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">Last Name</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">Job Title</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">Company</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">Email</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">Contact Type</th>
              <th className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-3 text-left">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="py-12 text-center text-gray-400">
                  <SpinnerGap size={20} className="inline animate-spin mr-2" />
                  Loading contacts…
                </td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan="8" className="py-12 text-center text-gray-400">
                  <UsersThree size={28} className="mx-auto mb-2 opacity-40" />
                  No contacts yet. Use Find Company Contacts on a company to discover and review them here.
                </td>
              </tr>
            ) : (
              contacts.map((c) => {
                const { first, last } = splitName(c);
                const generic = isGenericContact(c);
                return (
                  <tr
                    key={c.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${
                      selected.has(c.id) ? 'bg-emerald-50/40' : ''
                    }`}
                    data-testid={`review-contact-row-${c.id}`}
                  >
                    <td className="py-3 px-4">
                      <Checkbox
                        checked={selected.has(c.id)}
                        disabled={!c.email}
                        onCheckedChange={() => toggleSelect(c.id)}
                        data-testid={`review-contact-select-${c.id}`}
                      />
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-900">{first || '—'}</td>
                    <td className="py-3 px-3 text-sm text-gray-900">{last || '—'}</td>
                    <td className="py-3 px-3 text-sm text-gray-600">{c.job_title || '—'}</td>
                    <td className="py-3 px-3 text-sm text-gray-600">
                      {c.company_id ? (
                        <Link to={`/app/companies/${c.company_id}`} className="hover:underline">
                          {c.company_name || 'Company'}
                        </Link>
                      ) : (
                        c.company_name || '—'
                      )}
                    </td>
                    <td className="py-3 px-3 text-sm font-mono text-gray-700">{c.email}</td>
                    <td className="py-3 px-3">
                      <ContactTypeBadge contact={c} />
                    </td>
                    <td className="py-3 px-3">
                      {generic && !(c.confidence_score > 0) ? (
                        <span className="text-xs text-gray-400">—</span>
                      ) : (
                        <ConfidenceBadge score={c.confidence_score ?? 0} />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {total > contacts.length && (
        <p className="text-xs text-amber-700 mt-3 font-mono">
          Showing {contacts.length} of {total} contacts.
        </p>
      )}
    </div>
  );
}
